import React from 'react';
import { motion } from 'motion/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const springConfig = {
  type: "spring",
  damping: 80,
  stiffness: 400,
  mass: 1
};

const CookiePolicy = () => {
  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gradient-to-br from-orange-50 via-yellow-50 to-green-50">
        <div className="container mx-auto max-w-7xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
          >
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
              Cookie <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-yellow-500">Policy</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Last Updated: January 29, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springConfig, delay: 0.2 }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-gray-600 mb-6">
              Goodikids uses cookies and similar technologies to provide a safe, secure, and personalised learning experience for children and teens. This policy explains what cookies are, why we use them, and the choices available to parents and guardians.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">What Are Cookies?</h2>
            <p className="text-gray-600 mb-6">
              Cookies are small files stored on your device when you access the Goodikids app or website. They help the platform remember preferences, maintain secure login sessions, and improve overall performance.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Why We Use Cookies</h2>
            <p className="text-gray-600 mb-6">
              Goodikids uses cookies strictly to support learning, safety, and app functionality. We do not use them for advertising, cross-app tracking, or social media targeting.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Types of Cookies We Use</h2>
            
            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">1. Essential Cookies</h3>
            <p className="text-gray-600 mb-6">
              These cookies are required for the app to work correctly. They allow secure login, protect account authentication, and enable access to purchased courses and certificates. Without these cookies, the app cannot function properly.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">2. Performance Cookies</h3>
            <p className="text-gray-600 mb-6">
              Performance cookies help us monitor technical behaviour, reduce crashes, improve loading speed, and ensure smooth functioning across different devices and operating systems.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">3. Functionality Cookies</h3>
            <p className="text-gray-600 mb-6">
              These cookies make the learning experience more personalised. They remember the child’s language selection, avatar, age group, and course progress so that learning continues from where it stopped without setup each time.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-3 mt-8">4. Analytics Cookies</h3>
            <p className="text-gray-600 mb-6">
              Analytics cookies collect non-identifiable usage information such as screens visited or time spent on topics. This helps us improve content quality and user experience without tracking children across apps or websites.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Third-Party Cookies</h2>
            <p className="text-gray-600 mb-4">
              We may use trusted third-party services for app operation, such as:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Cloud hosting & databases</li>
              <li>Crash reporting & analytics</li>
              <li>Email/SMS delivery</li>
              <li>Payment processing (for subscriptions)</li>
            </ul>
            <p className="text-gray-600 mb-4">
              These partners:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Receive only the minimum necessary information</li>
              <li>Are not allowed to use data for advertising or marketing</li>
              <li>Must comply with child-privacy and confidentiality obligations</li>
            </ul>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Children’s Privacy & Parental Consent</h2>
            <p className="text-gray-600 mb-4">
              Goodikids is designed for ages 5–18 and follows global child-privacy regulations such as COPPA, GDPR-K, CCPA/CPRA, and Indian Data Protection Rules. We do not knowingly collect personal information from children through cookies without parental consent.
            </p>
            <p className="text-gray-600 mb-4">
              Parents/Guardians may request to:
            </p>
            <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
              <li>Access their child’s stored information</li>
              <li>Correct or update information</li>
              <li>Request deletion of information</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-600 mb-6">
              Requests can be sent to: <a href="mailto:privacy@goodikids.com" className="text-orange-500 hover:text-orange-600">privacy@goodikids.com</a>
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Managing Cookies</h2>
            <p className="text-gray-600 mb-4">
              Parents/Guardians may manage or disable cookies using the device/browser settings or app settings (if supported).
            </p>
            <p className="text-gray-600 mb-6">
              Please note: Disabling essential cookies may affect login, progress tracking, certificate access, and overall app performance.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Changes to This Policy</h2>
            <p className="text-gray-600 mb-6">
              We may update this Cookie Policy periodically to reflect new features or legal requirements. The “Last Updated” date will always show the most recent version. Continued use of Goodikids indicates acceptance of the updated policy.
            </p>

            <h2 className="text-3xl font-bold text-gray-900 mb-4 mt-12">Contact Us</h2>
            <p className="text-gray-600 mb-6">
              For questions about this Cookie Policy, you can reach us at:
              <br />
              📧 <a href="mailto:privacy@goodikids.com" className="text-orange-500 hover:text-orange-600">privacy@goodikids.com</a>
            </p>
            <p className="text-gray-500 text-sm mt-12">
              ©️ 2025 Goodikids. All rights reserved.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;